<template>
  <div>
    <!-- Loader -->
    <loading-panel :loading="loading"></loading-panel>

    <card>
      <!-- Tabs and Actions Header -->
      <div class="row mb-3">
        <div class="col">
          <div
            class="btn-group btn-group-toggle"
            :class="'float-left'"
            data-toggle="buttons"
          >
            <label
              v-for="(option, index) in categories"
              :key="index + j"
              class="btn btn-sm btn-primary btn-simple"
              :class="{ active: option.active }"
              :id="index"
            >
              <input
                type="radio"
                @click="switchTab(index)"
                name="options"
                autocomplete="off"
                :checked="option.active"
              />
              <span style="font-size: medium; font-weight: lighter" class="d-none d-sm-block">{{ option.name }}</span>
              <span class="d-block d-sm-none">
                <i :class="option.icon"></i>
              </span>
            </label>
          </div>
        </div>
        <div class="col text-right d-sm-block d-none" v-if="activeTabIndex === 0">
          <base-button @click="createNewTemplate()" type="primary" size="sm">
            <i class="tim-icons mr-1 el-icon-plus"></i> Create new template
          </base-button>
        </div>
        <div class="col text-right d-sm-none d-block" v-if="activeTabIndex === 0">
          <base-button @click="createNewTemplate()" type="primary" size="sm">
            <i class="tim-icons el-icon-plus"></i>
          </base-button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredTemplates.length === 0 && !loading" class="text-center py-5">
        <div class="font-icon-detail">
          <span style="font-size: 3em; color: #e3e3e3">
            <i class="tim-icons icon-laptop"></i>
          </span>
          <h4 class="mt-3" v-if="activeTabIndex === 0">No booking templates yet</h4>
          <h4 class="mt-3" v-else>No archived templates</h4>
          <p class="text-muted mb-4" v-if="activeTabIndex === 0">
            Create templates to customize how your booking pages look and behave for different audiences or promotions.
          </p>
          <base-button v-if="activeTabIndex === 0" type="primary" @click="createNewTemplate()">
            <i class="tim-icons icon-simple-add mr-2"></i> Create your first template
          </base-button>
        </div>
      </div>

      <!-- Table View -->
      <div v-if="filteredTemplates.length > 0" class="mt-3">
        <!-- Desktop Table -->
        <div class="d-none d-sm-block">
          <b-table
            hover
            :items="filteredTemplates"
            :fields="tableFields"
          >
            <!-- Template Info Column -->
            <template v-slot:cell(template_info)="row">
              <div class="d-flex align-items-center">
                <div class="template-preview-mini mr-3"
                     :style="{
                       backgroundColor: row.item.background_color || '#1e1e2f',
                       borderRadius: '6px',
                       width: '50px',
                       height: '40px',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center'
                     }">
                  <img v-if="row.item.logo && row.item.show_logo"
                       :src="row.item.logo"
                       alt="Logo"
                       style="max-height: 25px; max-width: 40px; object-fit: contain;" />
                  <i v-else class="tim-icons icon-laptop"
                     :style="{ fontSize: '1rem', color: row.item.text_color || '#ffffff' }"></i>
                </div>
                <div>
                  <div>{{ row.item.name || 'Untitled template' }}</div>
                  <div class="text-muted small">{{ row.item.description || 'No description' }}</div>
                </div>
              </div>
            </template>

            <!-- Status Column -->
            <template v-slot:cell(status)="row">
              <el-tooltip v-if="row.item.is_default === 1" content="Shown at your base booking URL" placement="top">
                <badge type="info" class="mr-1">main</badge>
              </el-tooltip>
              <badge :type="row.item.is_active === 1 ? 'success' : 'warning'">
                {{ row.item.is_active === 1 ? 'Live' : 'Draft' }}
              </badge>
            </template>

            <!-- Events Column -->
            <template v-slot:cell(games)="row">
              <span>
                {{ row.item.game_count || 0 }} event{{ (row.item.game_count || 0) !== 1 ? 's' : '' }}
              </span>
            </template>

            <!-- Last Modified Column -->
            <template v-slot:cell(last_modified)="row">
              <div v-if="row.item.updated_at">
                {{ getRelativeTime(row.item.updated_at) }}
              </div>
              <div class="text-muted" v-else>
                -
              </div>
            </template>

            <!-- Actions Column -->
            <template v-slot:cell(actions)="row">
              <el-dropdown>
                <base-button simple type="primary" link size="sm">
                  <i class="tim-icons el-icon-more"></i>
                </base-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <a v-if="activeTabIndex === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('configure', row.item)">
                      <i class="tim-icons icon-pencil text-default mr-2"></i> Edit template
                    </a>
                    <a v-if="activeTabIndex === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('preview', row.item)">
                      <i class="tim-icons icon-world text-default mr-2"></i> Go to booking site
                    </a>
                    <a v-if="activeTabIndex === 0 && row.item.is_default !== 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('setDefault', row.item)">
                      <i class="tim-icons icon-check-2 text-default mr-2"></i> Set as main
                    </a>
                    <a v-if="activeTabIndex === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('duplicate', row.item)">
                      <i class="tim-icons icon-single-copy-04 text-default mr-2"></i> Duplicate
                    </a>
                    <a v-if="activeTabIndex === 0 && row.item.is_active === 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('deactivate', row.item)">
                      <span class="inactive-dot mr-2"></span> Deactivate
                    </a>
                    <a v-if="activeTabIndex === 0 && row.item.is_active !== 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('activate', row.item)">
                      <span class="active-dot mr-2"></span> Activate
                    </a>
                    <hr v-if="activeTabIndex === 0">
                    <a v-if="activeTabIndex === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('archive', row.item)">
                      <i class="tim-icons icon-trash-simple text-default mr-2"></i> Archive
                    </a>
                    <a v-if="activeTabIndex === 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('restore', row.item)">
                      <i class="tim-icons icon-refresh-02 text-default mr-2"></i> Restore
                    </a>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
          </b-table>
        </div>

        <!-- Mobile Table -->
        <div class="d-block d-sm-none">
          <b-table
            hover
            :items="filteredTemplates"
            :fields="['templates']"
          >
            <template v-slot:head(templates)>
              <span>Templates: {{ filteredTemplates.length }}</span>
            </template>

            <template v-slot:cell(templates)="row">
              <div class="d-flex justify-content-between align-items-start">
                <div class="d-flex align-items-start">
                  <div class="template-preview-mini mr-3"
                       :style="{
                         backgroundColor: row.item.background_color || '#1e1e2f',
                         borderRadius: '6px',
                         width: '40px',
                         height: '35px',
                         display: 'flex',
                         alignItems: 'center',
                         justifyContent: 'center'
                       }">
                    <i class="tim-icons icon-laptop"
                       :style="{ fontSize: '0.9rem', color: row.item.text_color || '#ffffff' }"></i>
                  </div>
                  <div>
                    <p class="mb-1">{{ row.item.name || 'Untitled template' }}</p>
                    <div class="mb-2">
                      <badge v-if="row.item.is_default === 1" type="info" class="mr-1" style="font-size: 0.7rem;">main</badge>
                      <badge :type="row.item.is_active === 1 ? 'success' : 'warning'" style="font-size: 0.7rem;">
                        {{ row.item.is_active === 1 ? 'Live' : 'Draft' }}
                      </badge>
                    </div>
                    <small class="text-muted">{{ row.item.game_count || 0 }} events</small>
                  </div>
                </div>
                <el-dropdown>
                  <base-button simple type="primary" link size="sm">
                    <i class="tim-icons el-icon-more"></i>
                  </base-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <a v-if="activeTabIndex === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('configure', row.item)">
                        <i class="tim-icons icon-pencil text-default mr-2"></i> Edit template
                      </a>
                      <a v-if="activeTabIndex === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('preview', row.item)">
                        <i class="tim-icons icon-world text-default mr-2"></i> Go to booking site
                      </a>
                      <a v-if="activeTabIndex === 0 && row.item.is_default !== 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('setDefault', row.item)">
                        <i class="tim-icons icon-check-2 text-default mr-2"></i> Set as main
                      </a>
                      <a v-if="activeTabIndex === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('duplicate', row.item)">
                        <i class="tim-icons icon-single-copy-04 text-default mr-2"></i> Duplicate
                      </a>
                      <a v-if="activeTabIndex === 0 && row.item.is_active === 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('deactivate', row.item)">
                        <span class="inactive-dot mr-2"></span> Deactivate
                      </a>
                      <a v-if="activeTabIndex === 0 && row.item.is_active !== 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('activate', row.item)">
                        <span class="active-dot mr-2"></span> Activate
                      </a>
                      <hr v-if="activeTabIndex === 0">
                      <a v-if="activeTabIndex === 0" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('archive', row.item)">
                        <i class="tim-icons icon-trash-simple text-default mr-2"></i> Archive
                      </a>
                      <a v-if="activeTabIndex === 1" class="dropdown-item mt-1 mb-2" href="#" @click.prevent="handleAction('restore', row.item)">
                        <i class="tim-icons icon-refresh-02 text-default mr-2"></i> Restore
                      </a>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </template>
          </b-table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="row" v-if="totalRows > perPage">
        <div class="col d-flex justify-content-center">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @input="getBookingSiteSettings()"
          ></b-pagination>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import { API_LOCATION, BOOKING_URL } from "@/api-config";
import axios from "axios";
import swal from "sweetalert2";
import { Auth } from "aws-amplify";
import { Badge } from "@/components";
import { BTable, BPagination } from "bootstrap-vue";
import { Dropdown, DropdownMenu, Tooltip } from "element-ui";
import moment from "moment";

export default {
  name: "BookingSiteSettingsList",
  components: {
    Badge,
    BTable,
    BPagination,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [Tooltip.name]: Tooltip,
  },
  data() {
    return {
      companyId: localStorage.getItem('companyId'),
      companyGroupId: JSON.parse(localStorage.getItem('group')).id,
      groupCode: JSON.parse(localStorage.getItem('group')).code,
      loading: false,
      payment_settings: [],
      bookingSiteTemplates: [],
      filteredTemplates: [],
      j: 0,
      activeTabIndex: 0,
      categories: [
        { name: 'Active', icon: 'tim-icons icon-check-2', active: true },
        { name: 'Archived', icon: 'tim-icons icon-trash-simple', active: false }
      ],
      tableFields: [
        { key: 'template_info', label: 'Template' },
        { key: 'status', label: 'Status' },
        { key: 'games', label: 'Events' },
        { key: 'last_modified', label: 'Last modified' },
        { key: 'actions', label: 'Actions' }
      ],
      currentPage: 1,
      perPage: 10,
      totalRows: 0
    }
  },
  methods: {
    switchTab(index) {
      this.activeTabIndex = index
      for (let n in this.categories) {
        this.categories[n].active = false
      }
      this.categories[index].active = true
      this.j++
      this.filterTemplates()
    },
    filterTemplates() {
      if (this.activeTabIndex === 0) {
        // Active templates
        this.filteredTemplates = this.bookingSiteTemplates.filter(t => t.archived !== 1)
      } else {
        // Archived templates
        this.filteredTemplates = this.bookingSiteTemplates.filter(t => t.archived === 1)
      }
      this.totalRows = this.filteredTemplates.length
    },
    getRelativeTime(dateString) {
      if (!dateString) return '-'
      const now = moment()
      const inputDate = moment(dateString)
      const diffInDays = now.diff(inputDate, 'days')
      const diffInWeeks = now.diff(inputDate, 'weeks')
      const diffInMonths = now.diff(inputDate, 'months')

      if (diffInDays < 1) return 'Today'
      if (diffInDays === 1) return 'Yesterday'
      if (diffInDays < 7) return `${diffInDays} days ago`
      if (diffInWeeks === 1) return 'Last week'
      if (diffInWeeks < 4) return `${diffInWeeks} weeks ago`
      if (diffInMonths === 1) return 'Last month'
      return `${diffInMonths} months ago`
    },
    handleAction(command, item) {
      if (command === 'configure') {
        this.configureTemplate(item.id)
      } else if (command === 'preview') {
        this.previewTemplate(item)
      } else if (command === 'archive') {
        this.archiveTemplatePrompt(item)
      } else if (command === 'restore') {
        this.restoreTemplate(item)
      } else if (command === 'setDefault') {
        this.setAsDefaultPrompt(item)
      } else if (command === 'activate') {
        this.toggleActivation(item, 1)
      } else if (command === 'deactivate') {
        this.toggleActivation(item, 0)
      } else if (command === 'duplicate') {
        this.duplicateTemplate(item)
      }
    },
    configureTemplate(id) {
      this.$router.push({ path: '/bookings/settings/editor', query: { id: id }})
    },
    createNewTemplate() {
      this.$router.push({ path: '/bookings/settings/editor' })
    },
    getTemplateUrl(item) {
      let url = BOOKING_URL + this.groupCode
      if (item.is_default !== 1 && item.slug) {
        url += '/' + item.slug
      }
      return url
    },
    previewTemplate(item) {
      window.open(this.getTemplateUrl(item), '_blank')
    },
    archiveTemplatePrompt(item) {
      let message = `This will archive the "${item.name}" template. You can restore it later from the Archived tab.`
      if (item.is_default === 1) {
        message += ' Note: This is currently your main booking page. After archiving, visitors to your base booking URL will see no active template.'
      }

      swal({
        title: 'Are you sure?',
        text: message,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, archive it'
      }).then((response) => {
        if (response.value === true) {
          this.archiveTemplate(item)
        }
      })
    },
    async archiveTemplate(item) {
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token }}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/booking-site-settings/' + item.id

      axios.delete(url, config)
        .then((res) => {
          this.loading = false
          swal('Archived!', 'Template has been archived', 'success')
          this.getBookingSiteSettings()
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          swal('Error!', 'Something went wrong', 'error')
        })
    },
    async restoreTemplate(item) {
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token }}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/booking-site-settings/' + item.id + '/restore'

      axios.put(url, {}, config)
        .then((res) => {
          this.loading = false
          swal('Restored!', 'Template has been restored', 'success')
          this.getBookingSiteSettings()
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          swal('Error!', 'Something went wrong', 'error')
        })
    },
    setAsDefaultPrompt(item) {
      const currentDefault = this.bookingSiteTemplates.find(t => t.is_default === 1)
      let message = `"${item.name}" will be shown at your base booking URL (/${this.groupCode}).`
      if (currentDefault && currentDefault.id !== item.id) {
        message += ` "${currentDefault.name}" will still be accessible at /${this.groupCode}/${currentDefault.slug || 'its-slug'}.`
      }

      swal({
        title: 'Set as main template?',
        text: message,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, set as main'
      }).then((response) => {
        if (response.value === true) {
          this.setAsDefault(item)
        }
      })
    },
    async setAsDefault(item) {
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token }}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/booking-site-settings/' + item.id + '/set-default'

      axios.put(url, {}, config)
        .then((res) => {
          this.loading = false
          swal('Updated!', `"${item.name}" is now your main booking page`, 'success')
          this.getBookingSiteSettings()
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          swal('Error!', 'Something went wrong', 'error')
        })
    },
    async toggleActivation(item, activate) {
      if(activate){
        if(!this.payment_settings || !this.payment_settings.length){
          swal({
            title: 'Error!',
            text: `You need to set up a payment gateway before activating your booking site.`,
            type: 'error',
            confirmButtonColor: '#fd5d93',
            confirmButtonText: 'Confirm'
          })
          return
        }
      }
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token }}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/booking-site-settings/' + item.id + '/activate'

      axios.put(url, { is_active: activate }, config)
        .then((res) => {
          this.loading = false
          const status = activate === 1 ? 'activated' : 'deactivated'
          swal('Updated!', `Template has been ${status}`, 'success')
          this.getBookingSiteSettings()
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          swal('Error!', 'Something went wrong', 'error')
        })
    },
    async duplicateTemplate(item) {
      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token }}
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/booking-site-settings/' + item.id + '/duplicate'

      axios.post(url, {}, config)
        .then((res) => {
          this.loading = false
          this.$router.push({ path: '/bookings/settings/editor', query: { id: res.data.id }})
        })
        .catch((err) => {
          this.loading = false
          console.error(err)
          swal('Error!', 'Failed to duplicate template', 'error')
        })
    },
    getAuthToken() {
      return new Promise(function(resolve) {
        Auth.currentSession()
          .then(data => {
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async getBookingSiteSettings() {
      this.loading = true
      let url = API_LOCATION + 'companies/' + this.companyId + '/groups/' + this.companyGroupId + '/booking-site-settings'
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token }}

      axios.get(url, config)
        .then(response => {
          this.bookingSiteTemplates = response.data.booking_site_settings || response.data
          // Ensure it's always an array
          if (!Array.isArray(this.bookingSiteTemplates)) {
            this.bookingSiteTemplates = [this.bookingSiteTemplates]
          }
          this.payment_settings = response.data.payment_settings || []
          this.filterTemplates()
          this.loading = false
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          if (err.response && err.response.status == 401) {
            swal({
              title: 'Error',
              html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
              type: 'error',
            }).then((response) => {
              this.$router.push('/')
            })
          }
          else if (err.response && err.response.status == 500) {
            swal({
              title: 'Error',
              html: 'The feature has not been enabled as part of your subscription plan.',
              type: 'error',
            }).then((response) => {
              this.$router.push('/')
            })
          }
          else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
  },
  mounted() {
    this.getBookingSiteSettings()
  }
}
</script>

<style scoped>
.template-preview-mini {
  flex-shrink: 0;
}
.active-dot {
  height: 10px;
  width: 10px;
  background-color: #00f2c3;
  border-radius: 50%;
  display: inline-block;
}
.inactive-dot {
  height: 10px;
  width: 10px;
  background-color: #fd5d93;
  border-radius: 50%;
  display: inline-block;
}
</style>
